import React, { useMemo } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Chip, Container, Grid, NativeSelect, Stack, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import { useI18next } from 'gatsby-plugin-react-i18next';

import { getKey } from '../../legacy/helpers';
import { Category } from './type';
import { ExperienceCard } from './card';

// ********************************************************************************
// Experience main layout component, it renders the experience page with its
// articles, categories and tags.
// TODO: Define the types for the props
type Props = {
  articles: any[];
  categories: Category[];
  tags: any[];
  locale: string;
  base: string;
  category: {
    id: string;
    slug: string;
  };
  tag: any;
};

const Article = ({ item, locale, height }) => {
  if (!item) return null;
  const { node } = item;
  return (
    <ExperienceCard
      height={height}
      slug={getKey(node, 'slug')}
      title={getKey(node, `title_${locale}`)}
      image={getKey(node, 'image.childImageSharp.fluid')}
      content={getKey(item, `node.review_${locale}`)}
      tags={getKey(node, 'tags')}
    />
  );
};

export const ExperienceLayout: React.FC<Props> = ({ articles, categories, tags, locale, base, category, tag }) => {
  const { t } = useI18next();

  // == State =====================================================================
  const selectedTag = tag !== null ? `${base}${tag.slug}` : `/experience`;
  const categoryChips = useMemo(() => {
    return [
      {
        titlebutton: locale === 'en' ? 'All' : 'Todas',
        link: base,
        key: 'all_categories',
        activebutton: category === null,
      },
      ...categories.map((cat) => ({
        titlebutton: cat.label,
        link: cat.value,
        key: cat.key,
        activebutton: category !== null ? category.id === cat.key : false,
      })),
    ];
  }, [categories, category, locale, base]);

  // == Handlers ==================================================================
  const redirect = (slug: string) => {
    navigate(slug);
  };

  // == Render ====================================================================
  return (
    <Stack mb="2rem">
      <Box
        display="flex"
        minHeight="233px"
        bgcolor="#3F2B56"
        sx={{
          backgroundImage: `url(/images/Hero/deco.svg)`,
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ flex: 1, padding: '1rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          <Typography variant="h1" color="white" fontFamily="Gotham-Bold" fontSize={{ xs: '32px', lg: '42px' }}>
            {t('experience.title')}
          </Typography>
          <Typography variant="subtitle1" color="white" fontFamily="Gotham-Light" fontSize={{ xs: '12px', lg: '16px' }}>
            {t('experience.subtitle')}
          </Typography>
          <Box display="flex" mt="2rem" gap={3} flexWrap="wrap">
            <Box display="flex" gap={1} flexWrap="wrap">
              {categoryChips.map((item) => (
                <Chip
                  label={item.titlebutton}
                  key={item.key}
                  color="primary"
                  variant={item.activebutton ? 'filled' : 'outlined'}
                  onClick={() => redirect(item.link)}
                />
              ))}
            </Box>
            <NativeSelect
              disableUnderline
              size="small"
              variant="standard"
              value={selectedTag}
              onChange={(event) => redirect(event.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                padding: 0,
                color: 'white',
                fontSize: '14px',
                fontFamily: 'Gotham-Bold',
                ',.MuiNativeSelect-select': { padding: '0' },
                '.MuiNativeSelect-iconStandard': { color: 'white', fontSize: '20px' },
              }}
            >
              <option value="/experience">{locale === 'es' ? 'Todos los temas' : 'All topics'}</option>
              {tags.map((tag) => (
                <option key={tag.key} value={tag.value}>
                  {tag.label}
                </option>
              ))}
            </NativeSelect>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pt: '2rem', px: '1rem' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Article item={articles[0]} locale={locale} height={368} />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Article item={articles[1]} locale={locale} height="100%" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2} flex={1}>
                  <Article item={articles[2]} locale={locale} height={300} />
                  <Article item={articles[3]} locale={locale} height={300} />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
          {articles.slice(4).map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Article item={item} locale={locale} height={300} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Stack>
  );
};
